import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  ColorDisabled,
  ColorSuccess,
  ColorWarning,
  IgDialog,
  IgLink,
  IgTypography,
  SuccessIcon,
  WarningIcon,
} from "@intelligems/ig-theme";
import {
  ExperienceCategory,
  ExperienceDto,
  ThemeType,
} from "@intelligems/ig-types";
import LaunchIcon from "@mui/icons-material/Launch";
import { Box, CircularProgress, Grid, Skeleton } from "@mui/material";

import {
  useGetOrg,
  useGetOrgId,
} from "../../api/organizations/get-organization";
import { useGetThemes } from "../../api/shopify-themes/get-themes";
import { useGetScriptStatus } from "../../api/shops/get-script-status";
import { selectedExperience } from "../../store/slices/experiences";
import { getDialog, setDialog } from "../../store/slices/global";
import { getSliceSpecificRedirects } from "../../store/slices/redirects";
import { selectedVariations } from "../../store/slices/variations";
import { IAppState } from "../../store/types/store";
import { copyToClipboard } from "../../utils/copyToClipboard";
import {
  generatePreviewLink,
  getPreviewThemeId,
} from "../../utils/generatePreviewLink";
import { allIntegrationGuides } from "../../utils/linkSheet";
import openNewTab from "../../utils/openNewTab";
import { SelectTheme } from "../Select/SelectTheme";

export interface RedirectToPluginDialogProps {
  type: ExperienceCategory;
  handleClose?: () => void;
  onOpenClick?: (themeId?: string) => void;
  isLoading: boolean;
}

export const RedirectToPluginDialog = ({
  type,
  isLoading,
  onOpenClick,
  handleClose,
}: RedirectToPluginDialogProps) => {
  const experience = useSelector(selectedExperience);
  const themeRedirects = useSelector((state: IAppState) =>
    getSliceSpecificRedirects(state, "theme")
  );
  const dispatch = useDispatch();
  const [selectedTheme, setSelectedTheme] = useImmer<ThemeType | undefined>(
    undefined
  );
  const dialog = useSelector(getDialog("redirectToPluginDialog"));
  const props = dialog.props as {
    id: string;
    experience: ExperienceDto;
    path?: string;
  };

  const experienceToReference = useMemo(() => {
    if (experience) {
      return experience;
    } else return props?.experience;
  }, [experience, props?.experience]);

  const maybeThemeId = selectedTheme
    ? selectedTheme.role === "main"
      ? undefined
      : String(selectedTheme.id)
    : undefined;

  const org = useGetOrg();
  const orgId = useGetOrgId();
  const scriptStatus = useGetScriptStatus(org.data?.org?.id, maybeThemeId);

  const themes = useGetThemes();

  const loading = isLoading || !orgId;

  const handleDialogClose = () => {
    dispatch(setDialog({ open: false, dialog: "redirectToPluginDialog" }));
  };
  const title = useMemo(() => {
    if (loading) {
      return `Saving ${type}`;
    } else {
      return "Entering Preview Mode";
    }
  }, [type, loading]);

  useEffect(() => {
    if (themes.isSuccess) {
      setSelectedTheme(
        themes.data.themes.find((theme) => theme.role === "main")!
      );
    }
  }, [themes.isSuccess]);

  const onOpen = () => {
    if (onOpenClick) {
      onOpenClick(maybeThemeId);
      handleClose ? handleClose() : handleDialogClose();
    } else if (props?.id || experienceToReference.id) {
      if (org.isSuccess) {
        window.open(
          generatePreviewLink({
            organization: org.data.org,
            experience: experienceToReference,
            previewThemeId: getPreviewThemeId(themeRedirects),
          }),
          "_blank"
        );
      }
    }
  };

  return (
    <IgDialog
      showDialog={dialog.isOpen}
      handleClose={() => {
        handleClose ? handleClose() : handleDialogClose();
      }}
      handleNoClick={scriptStatus.data?.exists ? undefined : () => onOpen()}
      minWidth={"500px"}
      maxWidth={"lg"}
      dialogHeight={null}
      dialogContentWidth={"325px"}
      title={title}
      hideActions={isLoading}
      showCloseIcon={true}
      rawContent={
        loading ? (
          <Box style={{ marginTop: 6, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container item direction={"column"} rowSpacing={2}>
            <Grid item>
              <IgTypography>
                In preview mode you can add text replacements to your{" "}
                {type === "experiment" ? "test" : "personalization"} directly on
                your storefront.
              </IgTypography>
            </Grid>
            <Grid item>
              <IgTypography>
                Select from the dropdown below when integrating our script into
                your draft theme.
              </IgTypography>
            </Grid>
            <Grid item>
              {themes.isLoading ? (
                <Skeleton sx={{ width: "200px", height: "31px" }} />
              ) : (
                <SelectTheme
                  maxWidth={"300px"}
                  value={selectedTheme ? String(selectedTheme.id) : ""}
                  onChange={(value: string) => {
                    const theme = (themes.data?.themes || []).find(
                      (t) => String(t.id) === value
                    )!;
                    setSelectedTheme(theme);
                  }}
                />
              )}
            </Grid>
            <Grid
              container
              item
              direction={"column"}
              rowSpacing={1}
              sx={{
                marginTop: "15px",
                borderTop: `${ColorDisabled.light} solid 1px`,
                borderBottom: `${ColorDisabled.light} solid 1px`,
                padding: "10px",
                paddingTop: "2px !important",
                flexWrap: "nowrap",
              }}
              justifyContent={"spaceBetween"}
              alignItems={"center"}
            >
              <Grid
                container
                item
                sx={{ flexWrap: "nowrap" }}
                direction={"row"}
                columnSpacing={2}
                justifyContent={"spaceBetween"}
                alignItems={"center"}
              >
                <Grid
                  item
                  sx={{ height: scriptStatus.isLoading ? "31px" : undefined }}
                >
                  {scriptStatus.isLoading ? (
                    <Skeleton sx={{ width: "31px", height: "31px" }} />
                  ) : scriptStatus.data?.exists ? (
                    <SuccessIcon width={"35"} height={"31"} />
                  ) : (
                    <WarningIcon width={"35"} height={"31"} />
                  )}
                </Grid>
                <Grid item sx={{ textAlign: "left" }}>
                  {scriptStatus.isLoading ? (
                    <Skeleton sx={{ width: "237px", height: "31px" }} />
                  ) : (
                    <IgTypography
                      color={
                        scriptStatus.data?.exists
                          ? ColorSuccess.main
                          : ColorWarning.main
                      }
                    >
                      {scriptStatus.data?.exists
                        ? "The Intelligems Script is detected in this theme."
                        : "In order to use this feature you’ll need to install the Intelligems Script into this theme."}
                    </IgTypography>
                  )}
                </Grid>
              </Grid>
            </Grid>
            {scriptStatus.data && !scriptStatus.data.exists ? (
              <Grid item>
                <IgTypography>
                  Read our{" "}
                  <IgLink
                    onClick={() => {
                      openNewTab(allIntegrationGuides, "Integration Guides");
                    }}
                  >
                    help article
                  </IgLink>{" "}
                  for more information.
                </IgTypography>
              </Grid>
            ) : null}
          </Grid>
        )
      }
      handleYesClick={
        scriptStatus.data && scriptStatus.data.exists
          ? () => onOpen()
          : async () =>
              copyToClipboard(
                `<script src="https://cdn.intelligems.io/${orgId}.js"></script>`
              )
      }
      yesButtonText={
        scriptStatus.data && scriptStatus.data.exists ? (
          <span>
            <IgTypography color={"white"}>
              Continue to Selected Theme
            </IgTypography>
            <LaunchIcon sx={{ paddingLeft: "5px", fontSize: "16px" }} />
          </span>
        ) : (
          "Copy Script"
        )
      }
      noButtonText={
        isLoading
          ? null
          : scriptStatus.data && scriptStatus.data.exists
            ? null
            : "Continue to Selected Theme Anyways"
      }
    />
  );
};
